import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  CircularProgress,
  Tooltip,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TransmissionService } from '../../services/transmissionService';
import { styled } from '@mui/material/styles';

const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
}));

const TransmissionLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    direction: '',
    partnerType: '',
    partnerName: '',
    status: '',
    patientId: '',
    orderId: '',
  });

  const [limitCount, setLimitCount] = useState(100);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const logsData = await TransmissionService.getTransmissionLogs(filters, limitCount);
      setLogs(logsData);
    } catch (err) {
      console.error('Error fetching transmission logs:', err);
      setError('Failed to fetch transmission logs: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, limitCount]);

  const fetchStats = useCallback(async () => {
    try {
      const statsData = await TransmissionService.getTransmissionStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  const handleSearch = () => {
    fetchLogs();
  };

  const handleReset = () => {
    setFilters({
      startDate: null,
      endDate: null,
      direction: '',
      partnerType: '',
      partnerName: '',
      status: '',
      patientId: '',
      orderId: '',
    });
    setLimitCount(100);
  };

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setDetailDialogOpen(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircleIcon color="success" />;
      case 'ERROR':
        return <ErrorIcon color="error" />;
      case 'PENDING':
        return <ScheduleIcon color="warning" />;
      case 'RETRY':
        return <ReplayIcon color="info" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SUCCESS':
        return 'success';
      case 'ERROR':
        return 'error';
      case 'PENDING':
        return 'warning';
      case 'RETRY':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString();
  };

  const exportToCSV = () => {
    const headers = [
      'Timestamp',
      'Direction',
      'Partner Type',
      'Partner Name',
      'Message Type',
      'Status',
      'Error Message',
      'Patient ID',
      'Order ID',
      'Retry Count',
    ];

    const csvContent = [
      headers.join(','),
      ...logs.map(log => [
        formatDate(log.timestamp),
        log.direction,
        log.partner_type,
        log.partner_name,
        log.message_type,
        log.status,
        log.error_message || '',
        log.patient_id || '',
        log.order_id || '',
        log.retry_count,
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transmission_log_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: '20px' }}>
      <HeaderCard>
        <CardContent sx={{ py: 1.5, px: 3, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Transmission Log
          </Typography>
        </CardContent>
      </HeaderCard>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Transmission Log</Typography>
        <Box>
          <IconButton onClick={fetchLogs} disabled={loading}>
            <RefreshIcon />
          </IconButton>
          <IconButton onClick={exportToCSV} disabled={loading || logs.length === 0}>
            <DownloadIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Transmissions
                </Typography>
                <Typography variant="h4">{stats.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Successful
                </Typography>
                <Typography variant="h4" color="success.main">{stats.success}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Errors
                </Typography>
                <Typography variant="h4" color="error.main">{stats.error}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Pending
                </Typography>
                <Typography variant="h4" color="warning.main">{stats.pending}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper style={{ padding: '20px', marginBottom: '20px' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={filters.startDate}
                onChange={(date) => setFilters({ ...filters, startDate: date })}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={filters.endDate}
                onChange={(date) => setFilters({ ...filters, endDate: date })}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Direction</InputLabel>
              <Select
                value={filters.direction}
                onChange={(e) => setFilters({ ...filters, direction: e.target.value })}
                label="Direction"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="INBOUND">Inbound</MenuItem>
                <MenuItem value="OUTBOUND">Outbound</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Partner Type</InputLabel>
              <Select
                value={filters.partnerType}
                onChange={(e) => setFilters({ ...filters, partnerType: e.target.value })}
                label="Partner Type"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="LAB">Lab</MenuItem>
                <MenuItem value="IMAGING">Imaging</MenuItem>
                <MenuItem value="PHARMACY">Pharmacy</MenuItem>
                <MenuItem value="INSURANCE">Insurance</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="SUCCESS">Success</MenuItem>
                <MenuItem value="ERROR">Error</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="RETRY">Retry</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Partner Name"
              value={filters.partnerName}
              onChange={(e) => setFilters({ ...filters, partnerName: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Patient ID"
              value={filters.patientId}
              onChange={(e) => setFilters({ ...filters, patientId: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Order ID"
              value={filters.orderId}
              onChange={(e) => setFilters({ ...filters, orderId: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Limit"
              type="number"
              value={limitCount}
              onChange={(e) => setLimitCount(parseInt(e.target.value) || 100)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              fullWidth
            >
              Search
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              onClick={handleReset}
              fullWidth
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" style={{ marginBottom: '20px' }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Direction</TableCell>
                <TableCell>Partner</TableCell>
                <TableCell>Message Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Error Message</TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>Order ID</TableCell>
                <TableCell>Retry Count</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>{formatDate(log.timestamp)}</TableCell>
                  <TableCell>
                    <Chip
                      label={log.direction}
                      size="small"
                      color={log.direction === 'INBOUND' ? 'primary' : 'secondary'}
                    />
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {log.partner_name}
                      </Typography>
                      <Chip
                        label={log.partner_type}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>{log.message_type}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getStatusIcon(log.status)}
                      <Chip
                        label={log.status}
                        size="small"
                        color={getStatusColor(log.status)}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    {log.error_message && (
                      <Tooltip title={log.error_message} placement="top">
                        <Typography
                          variant="body2"
                          color="error"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {log.error_message}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    {log.patient_mrn || log.patient_name ? (
                      <Box>
                        {log.patient_name && (
                          <Typography variant="body2" fontWeight="bold">
                            {log.patient_name}
                          </Typography>
                        )}
                        <Typography variant="caption" color="textSecondary">
                          {log.patient_mrn || log.patient_id || '-'}
                        </Typography>
                      </Box>
                    ) : (
                      log.patient_id || '-'
                    )}
                  </TableCell>
                  <TableCell>{log.order_id || '-'}</TableCell>
                  <TableCell>{log.retry_count}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleViewDetails(log)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Transmission Log Details</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Timestamp</Typography>
                  <Typography variant="body1">{formatDate(selectedLog.timestamp)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Direction</Typography>
                  <Typography variant="body1">{selectedLog.direction}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Partner Name</Typography>
                  <Typography variant="body1">{selectedLog.partner_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Partner Type</Typography>
                  <Typography variant="body1">{selectedLog.partner_type}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Message Type</Typography>
                  <Typography variant="body1">{selectedLog.message_type}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Typography variant="body1">{selectedLog.status}</Typography>
                </Grid>
                {selectedLog.patient_name && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Patient Name</Typography>
                    <Typography variant="body1">{selectedLog.patient_name}</Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Patient MRN</Typography>
                  <Typography variant="body1">{selectedLog.patient_mrn || selectedLog.patient_id || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Order ID</Typography>
                  <Typography variant="body1">{selectedLog.order_id || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Retry Count</Typography>
                  <Typography variant="body1">{selectedLog.retry_count}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Message ID</Typography>
                  <Typography variant="body1">{selectedLog.message_id || '-'}</Typography>
                </Grid>
                {selectedLog.error_code && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Error Code</Typography>
                    <Typography variant="body1">{selectedLog.error_code}</Typography>
                  </Grid>
                )}
                {selectedLog.error_message && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Error Message</Typography>
                    <Typography variant="body1" color="error">
                      {selectedLog.error_message}
                    </Typography>
                  </Grid>
                )}
                {selectedLog.raw_message && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Raw Message</Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        backgroundColor: '#f5f5f5',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap',
                        maxHeight: 200,
                        overflow: 'auto',
                      }}
                    >
                      {selectedLog.raw_message}
                    </Paper>
                  </Grid>
                )}
                {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Metadata</Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        backgroundColor: '#f5f5f5',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                      }}
                    >
                      {JSON.stringify(selectedLog.metadata, null, 2)}
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TransmissionLog; 